import { type Aircraft, cruiseSpeed } from "../aircraft/Aircraft";
import {
  AIRCRAFT_SPECS,
  type AircraftKind,
  pickAircraftKind,
} from "../aircraft/AircraftCatalog";
import { type Airframe, airframeFor } from "../aircraft/Airframes";
import type {
  MutableVec2,
  Vec2,
  WorldBounds,
} from "../airport/AirportDefinition";
import type { DifficultyRamp } from "../config/gameplay";
import {
  advancePoint,
  angleTo,
  distanceToPolygonEdge,
  isInsideConvexPolygon,
  lerp,
  velocityFor,
} from "./geometry";

/** An inbound aircraft that is telegraphed before it enters the world. */
export interface SpawnWarning {
  readonly id: number;
  readonly kind: AircraftKind;
  /**
   * Chosen here, not when the aircraft is built, so the conflict scoring below
   * predicts the speed this arrival will actually fly at.
   */
  readonly airframe: Airframe;
  /** Index of the view edge it enters from, so two in a row differ. */
  readonly edge: number;
  readonly pos: MutableVec2;
  readonly entry: MutableVec2;
  readonly heading: number;
  warningRemaining: number;
  readonly warningTotal: number;
}

export interface Difficulty {
  readonly interval: number;
  readonly maxConcurrent: number;
}

export interface SpawnOptions {
  readonly lookaheadSeconds: number;
  readonly separationFactor: number;
  readonly departureSpeed: number;
  readonly offset: number;
  readonly attempts: number;
}

export type RandomSource = () => number;

/** How far off the middle of the view an arrival may be aimed, per axis. */
const AIM_SPREAD = 1 / 3;

/** Difficulty interpolates linearly over `rampSeconds`, then holds. */
export function difficultyAt(
  elapsedSeconds: number,
  ramp: DifficultyRamp,
  rampSeconds: number,
): Difficulty {
  const t = Math.min(1, elapsedSeconds / rampSeconds);
  return {
    interval: lerp(ramp.spawnIntervalStart, ramp.spawnIntervalEnd, t),
    maxConcurrent: Math.round(
      lerp(ramp.maxConcurrentStart, ramp.maxConcurrentEnd, t),
    ),
  };
}

function cornersOf(bounds: WorldBounds): readonly Vec2[] {
  return (
    bounds.corners ?? [
      { x: bounds.minX, y: bounds.maxY },
      { x: bounds.maxX, y: bounds.maxY },
      { x: bounds.maxX, y: bounds.minY },
      { x: bounds.minX, y: bounds.minY },
    ]
  );
}

/**
 * Pick a point on a view edge, step outside it and aim roughly at the middle
 * of the map. `excludeEdge` keeps consecutive arrivals from stacking up.
 */
export function createSpawnCandidate(
  bounds: WorldBounds,
  warningSeconds: number,
  id: number,
  random: RandomSource,
  excludeEdge: number | null = null,
  offset = 24,
): SpawnWarning {
  const corners = cornersOf(bounds);

  let edge = Math.floor(random() * (excludeEdge === null ? 4 : 3));
  if (excludeEdge !== null && edge >= excludeEdge) {
    edge++;
  }

  const from = corners[edge % corners.length]!;
  const to = corners[(edge + 1) % corners.length]!;
  const along = random();
  const entry = { x: lerp(from.x, to.x, along), y: lerp(from.y, to.y, along) };

  const centre = corners.reduce(
    (sum, corner) => ({
      x: sum.x + corner.x / corners.length,
      y: sum.y + corner.y / corners.length,
    }),
    { x: 0, y: 0 },
  );

  const edgeX = to.x - from.x;
  const edgeY = to.y - from.y;
  const edgeLength = Math.hypot(edgeX, edgeY) || 1;
  let normalX = -edgeY / edgeLength;
  let normalY = edgeX / edgeLength;
  if (normalX * (entry.x - centre.x) + normalY * (entry.y - centre.y) < 0) {
    normalX *= -1;
    normalY *= -1;
  }

  const pos = { x: entry.x + normalX * offset, y: entry.y + normalY * offset };

  // Aim inside the view's own quad, not its bounding box. Under an orbited
  // camera the box is far larger than the view, so box-relative jitter aims
  // arrivals past the corners and they skim the edge instead of flying in.
  const spanA = { x: (to.x - from.x) / 2, y: (to.y - from.y) / 2 };
  const next = corners[(edge + 2) % corners.length]!;
  const spanB = { x: (next.x - to.x) / 2, y: (next.y - to.y) / 2 };
  const alongA = lerp(-AIM_SPREAD, AIM_SPREAD, random());
  const alongB = lerp(-AIM_SPREAD, AIM_SPREAD, random());
  const heading = angleTo(pos, {
    x: centre.x + spanA.x * alongA + spanB.x * alongB,
    y: centre.y + spanA.y * alongA + spanB.y * alongB,
  });

  const kind = pickAircraftKind(random());

  return {
    id,
    kind,
    airframe: airframeFor(kind, id),
    edge,
    pos,
    entry,
    heading,
    warningRemaining: warningSeconds,
    warningTotal: warningSeconds,
  };
}

/**
 * Smallest separation this candidate would have against live traffic and
 * other pending arrivals over the look-ahead window. Negative means it would
 * be born too close to something.
 */
export function scoreSpawnCandidate(
  candidate: SpawnWarning,
  aircraft: readonly Aircraft[],
  pending: readonly SpawnWarning[],
  options: SpawnOptions,
): number {
  let worst = Number.POSITIVE_INFINITY;
  const spec = AIRCRAFT_SPECS[candidate.kind];
  const velocity = velocityFor(
    candidate.heading,
    spec.speed * candidate.airframe.speedFactor,
  );

  for (let t = 0; t <= options.lookaheadSeconds; t += 0.5) {
    const here = advancePoint(candidate.pos, velocity, t);
    const absolute = candidate.warningRemaining + t;

    for (const other of aircraft) {
      if (other.state !== "flying" && other.state !== "departing") {
        continue;
      }
      const speed =
        other.state === "departing"
          ? options.departureSpeed
          : cruiseSpeed(other);
      const there = advancePoint(
        other.pos,
        velocityFor(other.heading, speed),
        absolute,
      );
      worst = Math.min(
        worst,
        Math.hypot(here.x - there.x, here.y - there.y) -
          (spec.radius + other.spec.radius) * options.separationFactor,
      );
    }

    for (const other of pending) {
      if (absolute < other.warningRemaining) {
        continue;
      }
      const otherSpec = AIRCRAFT_SPECS[other.kind];
      const there = advancePoint(
        other.pos,
        velocityFor(
          other.heading,
          otherSpec.speed * other.airframe.speedFactor,
        ),
        absolute - other.warningRemaining,
      );
      worst = Math.min(
        worst,
        Math.hypot(here.x - there.x, here.y - there.y) -
          (spec.radius + otherSpec.radius) * options.separationFactor,
      );
    }
  }

  return worst;
}

/** First conflict-free candidate, or the least bad one after `attempts`. */
export function pickSpawnCandidate(
  bounds: WorldBounds,
  warningSeconds: number,
  id: number,
  aircraft: readonly Aircraft[],
  pending: readonly SpawnWarning[],
  excludeEdge: number | null,
  random: RandomSource,
  options: SpawnOptions,
): SpawnWarning {
  let best = createSpawnCandidate(
    bounds,
    warningSeconds,
    id,
    random,
    excludeEdge,
    options.offset,
  );
  let bestScore = scoreSpawnCandidate(best, aircraft, pending, options);
  if (bestScore >= 0) {
    return best;
  }

  for (let attempt = 1; attempt < options.attempts; attempt++) {
    const candidate = createSpawnCandidate(
      bounds,
      warningSeconds,
      id,
      random,
      excludeEdge,
      options.offset,
    );
    const score = scoreSpawnCandidate(candidate, aircraft, pending, options);
    if (score >= 0) {
      return candidate;
    }
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }

  return best;
}

/**
 * True once a point has left the (possibly rotated) view plus a margin.
 *
 * The rotated view is a convex quad, so this measures against the quad itself.
 * A circumscribed circle would be far more generous than the view at every
 * orbit that is not axis-aligned, letting aircraft loiter well off screen.
 */
export function isOutsideBounds(
  point: Vec2,
  bounds: WorldBounds,
  margin: number,
): boolean {
  const corners = bounds.corners;
  if (!corners || corners.length < 3) {
    return (
      point.x < bounds.minX - margin ||
      point.x > bounds.maxX + margin ||
      point.y < bounds.minY - margin ||
      point.y > bounds.maxY + margin
    );
  }

  if (isInsideConvexPolygon(point, corners)) {
    return false;
  }
  return distanceToPolygonEdge(point, corners) > margin;
}
